/**
 * Page /cee/dossiers/:id — detail dossier TRA-EQ-131.
 *
 * Sections :
 *  1. Header : nom client, mandataire, statut, velos
 *  2. Documents : grille des doc_type avec actions valider/rejeter/upload
 *  3. Notes : textarea avec auto-save debounced 500ms
 *  4. Actions : boutons Generer Devis/AH, Transmis, Bloquer, Relance
 *  5. Historique : timeline des cee_events
 *
 * Architecture : ce fichier orchestre les sous-sections (chaque < 100L).
 * Garde max 200 lignes en pilotant via les composants `sections/*.jsx`.
 *
 * Mock-first : si Track A/B endpoints pas encore deployes, on attrape l erreur
 * et on affiche un placeholder "(mock data)".
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as ceeApi from '../api/cee.js';
import DetailHeader from './sections/DetailHeader.jsx';
import DocumentsSection from './sections/DocumentsSection.jsx';
import NotesSection from './sections/NotesSection.jsx';
import ActionsSection from './sections/ActionsSection.jsx';
import EventTimeline from './components/EventTimeline.jsx';
import { useAuth } from '../auth/AuthContext.jsx';

export default function CeeDossierDetailPage() {
  const { id } = useParams();
  const { campaigns, activeCampaign } = useAuth();
  const [dossier, setDossier] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zipError, setZipError] = useState('');
  const activeRole = campaigns.find((c) => c.campaignId === activeCampaign)?.role;
  const readOnly = activeRole === 'partner';

  const reloadDossier = useCallback(async () => {
    try {
      const d = await ceeApi.getDossier(id);
      setDossier(d);
      return d;
    } catch (err) {
      setError(err.message || 'Erreur chargement dossier');
      return null;
    }
  }, [id]);

  const reloadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const evs = await ceeApi.getEvents(id, 50);
      setEvents(Array.isArray(evs) ? evs : []);
    } catch (_err) {
      // Track A/B pas encore deployee : on degrade gracieusement
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [id]);

  const reloadAll = useCallback(async () => {
    await Promise.all([reloadDossier(), reloadEvents()]);
  }, [reloadDossier, reloadEvents]);

  useEffect(() => {
    setLoading(true);
    setError('');
    reloadAll().finally(() => setLoading(false));
  }, [reloadAll]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-7 h-7 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (error && !dossier) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
        {error}
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="bg-white rounded-xl p-10 text-center text-[#64748b]">
        Dossier introuvable.
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <DetailHeader dossier={dossier} />
      {/* Codex P3 (commit 456a01d) : afficher l erreur ZIP en banner visible
          (le state error est rendu plus haut seulement si !dossier). */}
      {zipError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 mb-3 text-sm flex items-center justify-between gap-3">
          <span>{zipError}</span>
          <button type="button" onClick={() => setZipError('')} className="text-red-700 hover:text-red-900 font-bold">×</button>
        </div>
      )}
      <div className="mb-4">
        <button
          type="button"
          onClick={async () => {
            setZipError('');
            try {
              await ceeApi.downloadDossierZip(dossier.id);
            } catch (err) {
              setZipError(err.message || 'Échec du téléchargement ZIP');
            }
          }}
          className="inline-flex items-center gap-2 text-sm px-3 py-2 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-md font-medium transition-colors"
        >
          📦 Télécharger le dossier complet (ZIP)
        </button>
      </div>
      <DocumentsSection dossier={dossier} onChange={reloadAll} readOnly={readOnly} />
      <NotesSection dossier={dossier} readOnly={readOnly} />
      <ActionsSection dossier={dossier} onChange={reloadAll} readOnly={readOnly} />
      <section className="mb-6">
        <h2 className="text-lg font-bold text-[#0f172a] mb-3">Historique</h2>
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <EventTimeline events={events} loading={eventsLoading} limit={10} />
        </div>
      </section>
    </div>
  );
}
